import React, { useState } from 'react';
import { Info, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import defaultImg from "../Images/selfcleaning.avif";
import img from "../Images/tstain.jpeg";
import img1 from "../Images/boss.png";
import img2 from "../Images/duplex.jpeg";
import img3 from "../Images/conical.jpg";
import img4 from "../Images/ytype.webp";
import img5 from "../Images/basket.jpeg";
import img6 from "../Images/tstainspec.png";
import img7 from "../Images/concialspec.png";
import img8 from "../Images/ystainerspec.png";
import img9 from "../Images/bask.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article 
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6 lg:p-8"
      itemScope 
      itemType="https://schema.org/Product"
    >
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-gray-900" itemProp="name">
          {product.name}
        </h3>
      </header>
      
      <div className="bg-gray-100 h-48 sm:h-56 md:h-64 lg:h-72 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - Industrial filtration equipment by Gortherm Energy`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          itemProp="image"
          loading="lazy"
          width="600"
          height="400"
        />
      </div>

      <nav className="mb-4">
        <div className="flex border-b border-gray-200" role="tablist" aria-label="Product information">
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'description'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('description')}
            aria-selected={activeTab === 'description'}
            role="tab"
            aria-controls="description-panel"
          >
            Description
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === 'specs'
                ? 'border-b-2 border-teal-600 text-teal-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('specs')}
            aria-selected={activeTab === 'specs'}
            role="tab"
            aria-controls="specs-panel"
          >
            <span className="hidden sm:inline">Technical Specifications</span>
            <span className="sm:hidden">Specs</span>
          </button>
        </div>
      </nav>

      <div className="mt-4" role="tabpanel" id={activeTab === 'description' ? 'description-panel' : 'specs-panel'}>
        {activeTab === 'description' ? (
          <p className="text-gray-700 text-justify text-sm sm:text-base leading-relaxed" itemProp="description">
            {product.description}
          </p>
        ) : (
          <div>
            {product.specImage && (
              <div className="bg-gray-100 h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} detailed technical specifications and measurements`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            )}
            <ul className="space-y-2 sm:space-y-3">
              {product.specifications.map((spec, index) => (
                <li key={index} className="flex items-start">
                  <Info className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-700 text-sm sm:text-base">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
};

const Strainers = () => {
  const products = [
    {
      id: 'self-cleaning',
      name: 'Self Cleaning Filters',
      mainImage: defaultImg,
      specImage: "",
      description: `Our Self Cleaning Filters represent the pinnacle of automated filtration technology. These advanced systems provide continuous operation without interruption for cleaning or maintenance. The innovative self-cleaning mechanism automatically removes trapped particles from the filter element during operation, maintaining optimal flow rates and minimizing pressure drop. This design significantly reduces maintenance requirements and operating costs while ensuring consistent filtration efficiency. Perfect for applications requiring continuous operation and minimal maintenance intervention.`,
      specifications: [
        'Automatic cleaning cycles with programmable intervals',
        'Filter ratings from 25 to 3000 microns',
        'Operating pressure up to 40 bar',
        'Flow rates up to 3000 m³/hr',
        'Multiple cleaning mechanism options',
        'Available in carbon steel and stainless steel',
        'Automated purge valve system'
      ]
    },
    {
      id: 't-strainer',
      name: 'T-Strainers',
      mainImage: img,
      specImage: img6,
      description: `T-Strainers are engineered for high-capacity filtration in horizontal or vertical pipelines. Their T-shaped configuration provides excellent accessibility for screen maintenance while offering substantial screening area. The design allows for easy removal of the screen without disturbing the pipeline, reducing maintenance time and effort. These strainers are particularly effective in applications requiring frequent cleaning and inspection of the straining element.`,
      specifications: [
        'Sizes ranging from DN50 to DN600',
        'Pressure ratings up to ANSI Class 600',
        'Screen mesh sizes from 20 to 200 mesh',
        'Quick-release cover design',
        'Available in cast iron, carbon steel, and stainless steel',
        'Horizontal or vertical installation options',
        'Large debris collection capacity'
      ]
    },
    {
      id: 'box-strainer',
      name: 'Box Strainers',
      mainImage: img1,
      specImage: img6,
      description: `Box Strainers provide robust filtration solutions for large-volume applications. Their rectangular design offers maximum screening area and superior dirt-holding capacity. These strainers are ideal for protecting equipment in high-flow applications where maintaining continuous flow is critical. The box configuration allows for easy access to the straining element and simplified maintenance procedures, making them perfect for applications requiring frequent cleaning.`,
      specifications: [
        'Flow rates up to 5000 m³/hr',
        'Multiple screen options available',
        'Pressure ratings up to 20 bar',
        'Easy access cover design',
        'Drain and vent connections standard',
        'Available with magnetic inserts',
        'Custom sizes and configurations available'
      ]
    },
    {
      id: 'duplex-strainer',
      name: 'Duplex Strainers',
      mainImage: img2,
      specImage: img6,
      description: `Duplex Strainers feature two parallel straining chambers that allow for continuous operation during maintenance. The innovative design enables switching between chambers without interrupting flow, ensuring uninterrupted process operation. These strainers are ideal for applications where system shutdown for maintenance is not acceptable. The dual-chamber design provides complete isolation of the offline chamber for safe maintenance while maintaining system operation.`,
      specifications: [
        'Continuous operation capability',
        'Sizes from DN25 to DN300',
        'Pressure ratings up to ANSI Class 300',
        'Diverter valve with position indicator',
        'Multiple screen options',
        'Differential pressure monitoring ports',
        'Various material construction options'
      ]
    },
    {
      id: 'conical-strainer',
      name: 'Conical Strainers',
      mainImage: img3,
      specImage: img7,
      description: `Conical Strainers are designed for temporary or permanent installation in pipelines where directional flow and increasing open area are required. The conical screen design provides greater straining area than conventional cylindrical screens, resulting in lower pressure drop and higher dirt-holding capacity. These strainers are particularly effective in applications requiring high flow rates with minimal pressure loss.`,
      specifications: [
        'Conical screen design for maximum efficiency',
        'Available in temporary or permanent styles',
        'Multiple connection options',
        'Screen perforation sizes from 0.5mm to 12mm',
        'Suitable for horizontal or vertical installation',
        'Low pressure drop design',
        'High dirt-holding capacity'
      ]
    },
    {
      id: 'y-type',
      name: 'Y-Type Strainers',
      mainImage: img4,
      specImage: img8,
      description: `Y-Type Strainers offer efficient and economical filtration solutions for a wide range of applications. Their angular design provides excellent flow characteristics while maintaining adequate screening area. The streamlined flow path minimizes pressure drop, and the easy-access cover allows for quick screen cleaning and replacement. These versatile strainers are suitable for both liquid and gas applications across various industries.`,
      specifications: [
        'Sizes from DN15 to DN300',
        'Pressure ratings up to ANSI Class 600',
        'Screen options from 20 to 200 mesh',
        'Threaded or flanged connections',
        'Blow-off connection standard',
        'Multiple material options available',
        'Easy maintenance access'
      ]
    },
    {
      id: 'basket-strainer',
      name: 'Basket Strainers',
      mainImage: img5,
      specImage: img9,
      description: `Basket Strainers provide high-capacity filtration with their large screening area and substantial dirt-holding capacity. The vertical basket design allows debris to collect at the bottom of the basket, away from the screening surface, maintaining optimal flow rates for longer periods. These strainers are ideal for applications requiring high flow rates and extended periods between cleaning cycles. The quick-access cover design facilitates easy maintenance and basket cleaning.`,
      specifications: [
        'Large debris collection capacity',
        'Sizes from DN50 to DN600',
        'Multiple basket perforation options',
        'Quick-opening cover designs',
        'Differential pressure tappings',
        'Various material options',
        'Optional magnetic inserts available'
      ]
    }
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Strainers & Filtration Systems",
    "description": "Comprehensive range of industrial strainers including Y-type, basket, duplex, conical, and self-cleaning filters for process protection",
    "itemListElement": products.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": product.mainImage,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Industrial Strainers & Filtration Systems | Y-Type, Basket, Duplex | Gortherm Energy</title>
        <meta name="description" content="Comprehensive range of industrial strainers including Y-type strainers, basket strainers, duplex strainers, conical strainers, and self-cleaning filters. Superior filtration solutions for equipment protection and process efficiency." />
        <meta name="keywords" content="industrial strainers, Y-type strainer, basket strainer, duplex strainer, conical strainer, self-cleaning filters, pipeline filtration, process strainers, equipment protection, T-strainer, box strainer, filtration systems Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Strainers & Filtration Systems | Gortherm Energy" />
        <meta property="og:description" content="Superior filtration solutions for industrial applications. Complete range of strainers for equipment protection and optimal process performance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/strainers" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Strainers & Filtration Systems | Gortherm Energy" />
        <meta name="twitter:description" content="High-quality industrial strainers for equipment protection and process filtration in demanding applications." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/strainers" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <header className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Industrial Strainers & Filtration Systems
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Discover our comprehensive range of industrial strainers, engineered to provide superior filtration solutions for all your process protection requirements.
            </p>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </section>

          <aside className="mt-12 sm:mt-16 lg:mt-20 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Need Expert Guidance?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 px-4">
                Our specialists are here to help you select the perfect strainer
                solution for your application and ensure optimal equipment protection.
              </p>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Contact filtration experts at +977 980 278 1400"
              >
                <button className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span>Contact Our Experts</span>
                </button>
              </a>
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                Call us at: <a href="tel:+9779802781400" className="text-teal-600 hover:text-teal-700 font-semibold">+977 980 278 1400</a>
              </p>
            </div>
          </aside>

          {/* Additional SEO Content Section */}
          <section className="mt-12 sm:mt-16 bg-white rounded-xl p-6 sm:p-8 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Why Choose Gortherm Energy Strainers?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Equipment Protection</h3>
                <p className="text-sm sm:text-base">
                  Our strainers effectively remove debris, particles, and contaminants, protecting valuable 
                  downstream equipment from damage and extending operational life.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Versatile Solutions</h3>
                <p className="text-sm sm:text-base">
                  Complete range from Y-type to self-cleaning filters, offering the right solution for 
                  every application, flow rate, and maintenance requirement.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Easy Maintenance</h3>
                <p className="text-sm sm:text-base">
                  Designed for quick access and simple cleaning procedures, minimizing downtime and 
                  maintenance costs while ensuring optimal filtration performance.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Quality Construction</h3>
                <p className="text-sm sm:text-base">
                  Robust materials and precision engineering ensure reliable performance in demanding 
                  conditions with resistance to corrosion, pressure, and temperature extremes.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Strainers;